import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Securely validates and normalizes URLs to prevent SSRF attacks
 * @param {string} url - The URL to validate and normalize
 * @returns {string} - The normalized URL
 * @throws {Error} - If the URL is invalid or uses unsafe protocols/hosts
 */
export function validateAndNormalizeUrl(url) {
  if (!url || typeof url !== 'string') {
    throw new Error('URL must be a non-empty string')
  }

  let normalizedUrl

  // If URL contains protocol, validate it immediately
  if (url.includes('://')) {
    let parsedUrl
    try {
      parsedUrl = new URL(url)
    } catch (error) {
      throw new Error(`Invalid URL format: ${url}`)
    }

    // Only allow http and https protocols
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      throw new Error(`Unsafe URL protocol '${parsedUrl.protocol}'. Only http:// and https:// are allowed.`)
    }

    normalizedUrl = url
  } else {
    // If no protocol specified, prepend https://
    normalizedUrl = `https://${url}`
  }

  // Parse final URL and block private networks
  let parsedUrl = new URL(normalizedUrl)
  const hostname = parsedUrl.hostname.toLowerCase()

  // Block localhost and private IPs
  if (hostname === 'localhost' || 
      /^127\./.test(hostname) ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
    throw new Error('Access to private networks not allowed')
  }

  // Block other local addresses
  if (hostname === '0.0.0.0' || 
      hostname.startsWith('169.254.') || // Link-local
      hostname === '[::1]' || // IPv6 localhost
      hostname.match(/^fe80:/)) { // IPv6 link-local
    throw new Error('Access to private networks not allowed')
  }

  return normalizedUrl
}

/**
 * Checks if a URL is a LinkedIn company page URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a LinkedIn company URL
 */
export function isLinkedInCompanyUrl(url) {
  if (!url || typeof url !== 'string') {
    return false
  }
  
  try {
    // Handle URLs with or without protocol
    const urlToCheck = url.includes('://') ? url : `https://${url}`
    const parsedUrl = new URL(urlToCheck)
    const hostname = parsedUrl.hostname.toLowerCase()
    
    // Check if it's a LinkedIn domain and has /company/ in the path
    return (hostname === 'linkedin.com' || hostname === 'www.linkedin.com') &&
           parsedUrl.pathname.includes('/company/')
  } catch (error) {
    // If URL parsing fails, try string matching as fallback
    return url.includes('linkedin.com/company/')
  }
}
